import React from 'react';
import { Alert } from 'antd';

/**
 * `degradedStandards[]` on the ai-select-questions response
 * (`policylens-backend/src/controllers/scoping.controller.ts`,
 * `projects_id_gap_analysis_ai_select_questionsPost`) names every standard the
 * project itself selected (GDPR, ISO 27701, ISO 42001 — all genuinely
 * selectable in the scoping picker as of frontend commit e6ced11) that
 * currently has ZERO seeded question-bank content, meaning the questionnaire
 * silently fell back to the ISO 27001 baseline bank only.
 *
 * Reference-shape sibling: `GapAnalysisAiFailureBanner.jsx` — a small AntD
 * `Alert`-based component invoked via `jsx(ComponentName, {...})` from a
 * parent authored in compiled react/jsx-runtime call form (see
 * `ProjectOnboarding.jsx`'s own top-of-file imports).
 *
 * Visibility fix only (red-team follow-up to backend commits fc48ca5 /
 * a26132c): this banner never blocks or hides the picker/submit flow — GDPR /
 * ISO 27701 / ISO 42001 must stay selectable and the Advisor/LA must stay
 * able to proceed. It only tells the truth about what content is actually
 * being served. Commandment IV (AI Requires Human Approval) requires
 * explainability on every AI-served artefact; a served-but-mismatched
 * questionnaire with no on-screen signal is exactly the defect class this
 * closes.
 */
const DEGRADED_STANDARD_LABELS = {
  'eu.gdpr-2016': 'EU GDPR 2016/679',
  'iso.27701-2019': 'ISO/IEC 27701:2019',
  'iso.42001-2023': 'ISO/IEC 42001:2023',
};

function labelForDegradedStandard(id) {
  return DEGRADED_STANDARD_LABELS[id] || id;
}

export function GapAnalysisDegradedStandardsBanner({ degradedStandards = [] }) {
  if (!Array.isArray(degradedStandards) || degradedStandards.length === 0) return null;
  const names = degradedStandards.map(labelForDegradedStandard).join(', ');
  const plural = degradedStandards.length > 1;

  return (
    <Alert
      type="warning"
      showIcon
      message="Questionnaire shown is ISO 27001 baseline content only"
      description={
        `No seeded question-bank content exists yet for ${plural ? 'these standards' : 'this standard'} on ` +
        `this project: ${names}. The questions below are drawn from the ISO 27001 baseline bank and do NOT ` +
        `yet cover your project's own selected standard. You can still continue - nothing here is blocked - ` +
        `but review coverage manually until standard-specific question content is seeded.`
      }
      style={{ marginBottom: 16 }}
    />
  );
}

export default GapAnalysisDegradedStandardsBanner;
